/**
 * Fixtures do modo mock das telas de CRM (campanhas de marketing).
 *
 * Cobre:
 *  - GET /pipeline            → 3 painéis (templates Comercial/Produção/Financeiro)
 *  - GET /pipeline-stage      → board do painel de Produção, com cards por produto
 *  - GET /crm-tag             → tags do CRM
 *
 * Valores monetários em CENTAVOS (o front divide por 100). Para uma nova
 * captura, edite os arrays de cards/painéis aqui.
 */

import moment from "moment";

const brl = reais => Math.round(reais * 100);

// ---------------------------------------------------------------------------
// Pessoas (responsáveis / membros)
// ---------------------------------------------------------------------------

const ADMINS = {
    marina: { id: 1, name: "Marina Souza", photo_url: null },
    carlos: { id: 2, name: "Carlos Mendes", photo_url: null },
    ana: { id: 3, name: "Ana Beatriz Costa", photo_url: null },
    rafael: { id: 4, name: "Rafael Almeida", photo_url: null },
    juliana: { id: 5, name: "Juliana Ribeiro", photo_url: null },
    pedro: { id: 6, name: "Pedro Henrique Lima", photo_url: null },
};

// ---------------------------------------------------------------------------
// Produtos REAIS do tenant Kopu (origin `integracao_*`), com a URL pública do
// fornecedor (coluna product_images.integration_url). Categorias pedidas:
// squeeze, caneta, caderno, mochila.
// ---------------------------------------------------------------------------

const PRODUCTS = {
    squeezeTritan: { sku: "94620",   name: "Squeeze em Tritan 800 mL",      img: "https://www.spotgifts.com.br/fotos/produtos/94620_set.jpg" },
    squeezeInox:   { sku: "P@07088", name: "Squeeze Inox 550 mL",           img: "https://www.xbzbrindes.com.br/img/produtos/original/Squeeze-Inox-550ml-20486-1725457117.jpg" },
    canetaTouch:   { sku: "P@14205", name: "Caneta Metal Touch",            img: "https://www.xbzbrindes.com.br/img/produtos/3/Caneta-Metal-Touch-VERDE-9903-1561639996.jpg" },
    canetaMetal:   { sku: "01018",   name: "Caneta Metal Esferográfica",    img: "https://www.xbzbrindes.com.br/img/produtos/original/Caneta-Metal-Esferografica-PRETO-17079-1693310068.jpg" },
    cadernoFlex:   { sku: "53626",   name: "Caderno A5 Capa Flexível",      img: "https://www.spotgifts.com.br/fotos/produtos/53626_set.jpg" },
    cadernoPalha:  { sku: "53632",   name: "Caderno A5 Capa Dura Palha",    img: "https://www.spotgifts.com.br/fotos/produtos/53632_set.jpg" },
    mochilaPoli:   { sku: "14073",   name: "Mochila Poliéster p/ Notebook", img: "https://www.xbzbrindes.com.br/img/produtos/3/Mochila-Poliester-para-Notebook-9049-1547496813.jpg" },
    mochilaNote:   { sku: "92636",   name: 'Mochila Notebook 15,6" 600D',   img: "https://www.spotgifts.com.br/fotos/produtos/92636_set.jpg" },
};

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

const TAGS = {
    urgente: { id: 1, name: "Urgente", color: "#E05B6B" },
    externa: { id: 2, name: "Compra externa", color: "#4C7DE0" },
    importado: { id: 3, name: "Importado", color: "#E0A33C" },
    laser: { id: 4, name: "Gravação a laser", color: "#6C4CE0" },
    pronto: { id: 5, name: "Pronto p/ envio", color: "#2FBF8F" },
};

// ---------------------------------------------------------------------------
// Cards do painel de Produção (um card por produto, como no template)
// ---------------------------------------------------------------------------

const today = moment().format("YYYY-MM-DD");
const inDays = n => moment().add(n, "days").format("YYYY-MM-DD");

let cardSeq = 1;
const makeCard = ({ code, company, city, state, customer, product, qty, amount, admin, days, tags = [], task = null }) => ({
    id: cardSeq++,
    code,
    amount: brl(amount),
    status: "open",
    color: "",
    days_in_stage: days,
    company: { trade_name: company, city, state },
    customer: { name: customer },
    admin,
    crm_tags: tags,
    next_pending_task: task,
    order_item: {
        image_url: { small: product.img },
        product_name: product.name,
        subproduct_name: null,
        quantity: qty,
    },
});

// Cada entrada = uma etapa do template Produção, com seus cards.
const STAGES_SPEC = [
    {
        id: 201, name: "Pedido recebido", cards: [
            makeCard({ code: "10432", company: "Construtora Horizonte", city: "São Paulo", state: "SP", customer: "João Prado", product: PRODUCTS.squeezeTritan, qty: 500, amount: 12450, admin: ADMINS.marina, days: 1, tags: [TAGS.urgente] }),
            makeCard({ code: "10433", company: "Farmácias VidaPlena", city: "Campinas", state: "SP", customer: "Beatriz Nunes", product: PRODUCTS.canetaTouch, qty: 1000, amount: 8700, admin: ADMINS.carlos, days: 2 }),
            makeCard({ code: "10435", company: "Colégio Novo Saber", city: "Curitiba", state: "PR", customer: "Marta Reis", product: PRODUCTS.cadernoFlex, qty: 800, amount: 15200, admin: ADMINS.ana, days: 1, task: { status: "pending", type: "call", date: today } }),
        ],
    },
    {
        id: 202, name: "Compras", cards: [
            makeCard({ code: "10428", company: "AgroTech Brasil", city: "Ribeirão Preto", state: "SP", customer: "Ricardo Melo", product: PRODUCTS.mochilaPoli, qty: 150, amount: 22375, admin: ADMINS.rafael, days: 4 }),
            makeCard({ code: "10430", company: "Banco Meridiano", city: "São Paulo", state: "SP", customer: "Sofia Cardoso", product: PRODUCTS.squeezeInox, qty: 600, amount: 9900, admin: ADMINS.juliana, days: 3, tags: [TAGS.externa] }),
            makeCard({ code: "10425", company: "Clínica Bem Estar", city: "Belo Horizonte", state: "MG", customer: "Paulo Vieira", product: PRODUCTS.canetaMetal, qty: 1200, amount: 11600, admin: ADMINS.pedro, days: 5, task: { status: "pending", type: "email", date: inDays(-2) } }),
        ],
    },
    {
        id: 203, name: "Aguardando chegada", cards: [
            makeCard({ code: "10419", company: "Logística Rota Certa", city: "Guarulhos", state: "SP", customer: "Helena Dias", product: PRODUCTS.mochilaNote, qty: 120, amount: 27750, admin: ADMINS.marina, days: 8 }),
            makeCard({ code: "10421", company: "Universidade Aurora", city: "Rio de Janeiro", state: "RJ", customer: "Tiago Barros", product: PRODUCTS.cadernoPalha, qty: 400, amount: 18360, admin: ADMINS.carlos, days: 6, tags: [TAGS.importado] }),
        ],
    },
    {
        id: 204, name: "Personalização", cards: [
            makeCard({ code: "10410", company: "Supermercados União", city: "Osasco", state: "SP", customer: "Camila Rocha", product: PRODUCTS.squeezeTritan, qty: 700, amount: 14940, admin: ADMINS.ana, days: 3, task: { status: "pending", type: "task", date: inDays(1) } }),
            makeCard({ code: "10412", company: "Tech Solutions", city: "Florianópolis", state: "SC", customer: "André Lopes", product: PRODUCTS.canetaTouch, qty: 900, amount: 13050, admin: ADMINS.rafael, days: 2 }),
            makeCard({ code: "10415", company: "Construtora Horizonte", city: "São Paulo", state: "SP", customer: "João Prado", product: PRODUCTS.cadernoFlex, qty: 500, amount: 9500, admin: ADMINS.juliana, days: 4, tags: [TAGS.laser] }),
        ],
    },
    {
        id: 205, name: "Controle de qualidade", cards: [
            makeCard({ code: "10402", company: "Farmácias VidaPlena", city: "Campinas", state: "SP", customer: "Beatriz Nunes", product: PRODUCTS.mochilaPoli, qty: 200, amount: 17900, admin: ADMINS.pedro, days: 1 }),
            makeCard({ code: "10405", company: "Banco Meridiano", city: "São Paulo", state: "SP", customer: "Sofia Cardoso", product: PRODUCTS.squeezeInox, qty: 800, amount: 7920, admin: ADMINS.marina, days: 2 }),
        ],
    },
    {
        id: 206, name: "Separação", cards: [
            makeCard({ code: "10395", company: "Colégio Novo Saber", city: "Curitiba", state: "PR", customer: "Marta Reis", product: PRODUCTS.mochilaNote, qty: 100, amount: 18500, admin: ADMINS.carlos, days: 1, tags: [TAGS.pronto] }),
            makeCard({ code: "10398", company: "AgroTech Brasil", city: "Ribeirão Preto", state: "SP", customer: "Ricardo Melo", product: PRODUCTS.cadernoPalha, qty: 300, amount: 13770, admin: ADMINS.ana, days: 2 }),
        ],
    },
];

const stageBlock = spec => {
    const total = spec.cards.length;
    const totalValue = spec.cards.reduce((sum, c) => sum + c.amount, 0);
    return {
        id: spec.id,
        name: spec.name,
        cards: spec.cards,
        cards_pagination: { total, total_value: totalValue, current_page: 1, last_page: 1, per_page: 20 },
    };
};

const PRODUCTION_STAGES = STAGES_SPEC.map(stageBlock);
const productionTotalCards = PRODUCTION_STAGES.reduce((s, st) => s + st.cards_pagination.total, 0);
const productionTotalValue = PRODUCTION_STAGES.reduce((s, st) => s + st.cards_pagination.total_value, 0);

export const PIPELINE_STAGE_RESPONSE = {
    stages: PRODUCTION_STAGES,
    total_cards: productionTotalCards,
    total_value: productionTotalValue,
};

// ---------------------------------------------------------------------------
// Lista plana (GET /pipeline-stage/cards-list) — visão em lista do painel.
// Reaproveita os cards do board, achatando as etapas e injetando `stage_name`.
// ---------------------------------------------------------------------------

const LIST_CARDS = PRODUCTION_STAGES.flatMap(stage =>
    stage.cards.map(card => ({ ...card, stage_name: stage.name }))
);

// Nome propositalmente longo para validar o truncamento (ellipsis) do nome do
// produto na célula da lista.
if (LIST_CARDS[0]) {
    LIST_CARDS[0] = {
        ...LIST_CARDS[0],
        order_item: {
            ...LIST_CARDS[0].order_item,
            subproduct_name: "Squeeze em Tritan 800 mL — Tampa Rosca com Alça de Silicone e Gravação a Laser",
        },
    };
}

export const CARDS_LIST_RESPONSE = {
    cards: LIST_CARDS,
    pagination: { current_page: 1, last_page: 1, per_page: 20, total: LIST_CARDS.length },
    total_cards: productionTotalCards,
    total_value: productionTotalValue,
};

// ---------------------------------------------------------------------------
// Lista de painéis (GET /pipeline) — os 3 templates
// ---------------------------------------------------------------------------

const membersOf = (...keys) => keys.map(k => ADMINS[k]);

// Etapas "leves" (só nome + open_cards) para os cards de painel em /crm.
const lightStages = (baseId, entries) =>
    entries.map(([name, openCards], i) => ({ id: baseId + i, name, open_cards: openCards }));

// Painel de Produção: deriva os números reais dos cards acima.
const producaoStageCounts = PRODUCTION_STAGES.map(st => [st.name, st.cards_pagination.total]);

const PRODUCAO_PIPELINE = {
    id: 2,
    name: "Produção",
    icon: "🏭",
    stages: lightStages(201, producaoStageCounts),
    total_cards: productionTotalCards,
    open_cards: productionTotalCards,
    open_value: productionTotalValue,
    automations_count: 1,
    members: membersOf("marina", "carlos", "ana", "rafael"),
};

const COMERCIAL_PIPELINE = {
    id: 1,
    name: "Comercial",
    icon: "💼",
    stages: lightStages(101, [
        ["Orçamento", 34],
        ["Follow up", 21],
        ["Amostra virtual", 12],
        ["Contrato", 8],
    ]),
    total_cards: 128,
    open_cards: 75,
    open_value: brl(842300),
    automations_count: 1,
    members: membersOf("marina", "carlos", "ana", "rafael", "juliana", "pedro"),
};

const FINANCEIRO_PIPELINE = {
    id: 3,
    name: "Financeiro",
    icon: "💰",
    stages: lightStages(301, [
        ["Emissão de nota", 14],
        ["Pagamento parcial", 9],
        ["Pagamento confirmado", 22],
    ]),
    total_cards: 96,
    open_cards: 45,
    open_value: brl(613720),
    automations_count: 1,
    members: membersOf("juliana", "pedro"),
};

export const PIPELINE_RESPONSE = {
    resources: [COMERCIAL_PIPELINE, PRODUCAO_PIPELINE, FINANCEIRO_PIPELINE],
};

export const CRM_TAG_RESPONSE = {
    resources: Object.values(TAGS),
};
